import React, { useState } from 'react';
import { IAuditoriaSocietariaResultado, IItemCapitalSocial } from '../interfaces/IAuditoriaSocietaria';
import { formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaSocietariaResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

// COR-296: drill-down a nível de lançamento — o ERRO de divergência precisa mostrar não só o
// saldo consolidado, mas os lançamentos reais que compõem o saldo contábil da conta divergente.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemCapitalSocial }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong className='fs-8'>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-229: rótulo amigável por categoria, para a seção de contas auxiliares (nunca comparadas
// individualmente contra o Capital Social total da Receita Federal/JUCESP).
const rotuloCategoriaAuxiliar: Record<string, string> = {
  AFAC: 'AFAC / Recursos para Aumento de Capital',
  CAPITAL_A_INTEGRALIZAR: 'Capital a Integralizar',
  SOCIOS_QUOTAS: 'Sócios / Quotas',
};

const AuditoriaSocietariaDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const todasContas = resultado.capitalSocial || [];

  // COR-229: só a categoria CAPITAL_SOCIAL (ou item legado sem categoria) é confrontada contra a
  // Receita Federal/JUCESP — AFAC/Sócios-Quotas/Capital a Integralizar vão para a seção auxiliar.
  const capitalSocial = todasContas.filter(
    (item) => !item.categoriaCapital || item.categoriaCapital === 'CAPITAL_SOCIAL'
  );
  const contasAuxiliares = todasContas.filter(
    (item) => item.categoriaCapital && item.categoriaCapital !== 'CAPITAL_SOCIAL'
  );

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria Societária — Capital Social vs. Receita Federal — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>
              Multa ECD potencial — 5% sobre a divergência entre saldo ECD e registro na Receita Federal, limitada a 1% da receita bruta do período (Art. 12, II, Lei 8.218/1991).
            </div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Capital Social / AFAC e Auxiliares</div>
          <div className='auditoria-kpi-value'>
            {capitalSocial.length} / <span className='auditoria-contador auditoria-contador-aviso'>{contasAuxiliares.length}</span>
          </div>
          <div className='auditoria-kpi-help'>
            Capital Social/Capital Subscrito consolidado, confrontado com a Receita Federal/JUCESP — separado de AFAC,
            Sócios/Quotas e Capital a Integralizar, que nunca são comparados individualmente.
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>
            ERRO: divergência identificada. AVISO: API da Receita Federal indisponível. ALERTA: tolerância excedida.
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>CPC 00 (R2), ITG 2000 (R1), Código Civil Arts. 997/1.055, Lei 8.218/1991 Art. 12.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Base Legal — Capital Social:</strong> {resultado.fundamentacaoLegal}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Capital Social — Cruzamento ECD vs. Receita Federal</h3>
        <p className='text-muted'>
          Saldo consolidado da(s) conta(s) de Capital Social / Capital Subscrito / Capital Realizado (soma do saldo
          inicial mais os lançamentos do período), confrontado com o Capital Social informado no cadastro nacional
          CNPJ (Receita Federal / base JUCESP). Contas de AFAC, Sócios/Quotas e Capital a Integralizar não entram
          nesta comparação — veja a seção "Contas Auxiliares" abaixo. Clique em uma linha para ver o diagnóstico e a
          memória de cálculo.
        </p>

        {capitalSocial.length === 0 && (
          <div className='text-muted mt-4'>
            Nenhuma conta principal de Capital Social/Capital Subscrito identificada na ECD do período selecionado.
          </div>
        )}

        {capitalSocial.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Empresa / Conta</th>
                  <th className='text-end'>Saldo ECD</th>
                  <th className='text-end'>Registrado na RF</th>
                  <th className='text-end'>Divergência</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Fonte</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {capitalSocial.map((item: IItemCapitalSocial, idx) => {
                  const chave = `cs-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <div><strong>{item.empresaNome}</strong></div>
                          <div className='text-muted' style={{ fontSize: '12px' }}>
                            {item.contaContabil} — {item.descricaoConta || item.contaReferencial}
                          </div>
                          <div className='text-muted' style={{ fontSize: '11px' }}>CNPJ: {item.cnpj}</div>
                        </td>
                        <td className='text-end'>{formatCurrency(item.saldoContabil)}</td>
                        <td className='text-end'>
                          {item.capitalSocialRegistrado > 0
                            ? formatCurrency(item.capitalSocialRegistrado)
                            : <span className='text-muted'>—</span>}
                        </td>
                        <td className='text-end'>
                          {item.divergencia > 0
                            ? <strong style={{ color: '#9b1c1c' }}>{formatCurrency(item.divergencia)}</strong>
                            : <span className='text-muted'>—</span>}
                        </td>
                        <td className='text-end'>
                          {item.multaDivergencia > 0
                            ? <strong style={{ color: '#9b1c1c' }}>{formatCurrency(item.multaDivergencia)}</strong>
                            : <span className='text-muted'>—</span>}
                        </td>
                        <td>
                          <span style={{ fontSize: '11px', color: '#6b7280' }}>{item.fonteConsulta || '—'}</span>
                        </td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              {item.memoriaCalculo && (
                                <div className='mt-1'><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              )}
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {contasAuxiliares.length > 0 && (
        <div className='auditoria-card mt-4'>
          <h3>Contas Auxiliares — AFAC, Sócios/Quotas e Capital a Integralizar</h3>
          <p className='text-muted'>
            Subcontas do grupo de Capital Social que NÃO representam, isoladamente, o capital social registrado na
            Receita Federal/JUCESP — por isso nunca geram divergência nem multa de Capital Social. Contas de AFAC
            requerem auditoria societária/documental própria (contrato, ata, alteração contratual, prazo de
            integralização).
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Empresa / Conta</th>
                  <th>Categoria</th>
                  <th className='text-end'>Saldo ECD</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasAuxiliares.map((item: IItemCapitalSocial, idx) => {
                  const chave = `cs-aux-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <div><strong>{item.empresaNome}</strong></div>
                          <div className='text-muted' style={{ fontSize: '12px' }}>
                            {item.contaContabil} — {item.descricaoConta || item.contaReferencial}
                          </div>
                        </td>
                        <td>
                          <span className='auditoria-status status-AVISO'>
                            {rotuloCategoriaAuxiliar[item.categoriaCapital] || item.categoriaCapital}
                          </span>
                        </td>
                        <td className='text-end'>{formatCurrency(item.saldoContabil)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaSocietariaDetalhe;
